<script>
import { glSlotsMixin } from '~/lib/utils/vue3compat/gl_slots_mixin';
import { slashCommands } from '../../services/plugin_capabilities';
import { DuoChatPluginRegistry } from '../../services/plugin_registry';
import SlashCommandsMenu from './slash_commands_menu.vue';

/**
 * The slash-command menu wired to the Duo Chat plugin registry: it asks the plugins for
 * the commands available in the current chat context and hands the answer to the menu,
 * which stays a presentation component.
 *
 * Asked for on mount, and again whenever the chat context changes. Holding off until
 * the user types a `/` is worth doing once a plugin fetches over the network, and it
 * needs a loading state in the menu to go with it; both are follow-up work.
 */
export default {
  name: 'SlashCommandsMenuWithPlugins',
  components: { SlashCommandsMenu },
  mixins: [glSlotsMixin],
  inject: {
    duoChatPluginRegistry: {
      default: () => new DuoChatPluginRegistry(),
    },
  },
  props: {
    value: {
      type: String,
      required: true,
    },
    duoChatContext: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    inputSelector: {
      type: String,
      required: false,
      default: undefined,
    },
  },
  emits: ['select'],
  data() {
    return {
      commands: [],
    };
  },
  watch: {
    duoChatContext() {
      // Commands can be scoped to the project or resource the chat is pointed at, so
      // what we are holding no longer applies.
      this.loadCommands();
    },
  },
  mounted() {
    this.loadCommands();
  },
  methods: {
    async loadCommands() {
      const context = this.duoChatContext;

      const commands = await slashCommands.resolve(this.duoChatPluginRegistry.plugins, {
        apollo: this.$apollo,
        duoChatContext: context,
      });

      // The context moved on while this was in flight, so these commands are for a chat
      // that is no longer on screen.
      if (context !== this.duoChatContext) return;

      this.commands = commands;
    },
    onSelect(...args) {
      this.$emit('select', ...args);
    },
  },
};
</script>

<template>
  <slash-commands-menu
    :commands="commands"
    :value="value"
    :input-selector="inputSelector"
    @select="onSelect"
  >
    <template v-if="glSlots().default"><slot></slot></template>
  </slash-commands-menu>
</template>
